/**
 * Canvas drawing utility functions
 * Pure functions for drawing operations on HTML5 Canvas
 */

import {
	ANGLE_AVERAGE_POINTS,
	ARROW_ANGLE_DEGREES,
	ARROW_LENGTH_MULTIPLIER,
	COMPOSITE_DESTINATION_OUT,
	COMPOSITE_SOURCE_OVER,
	DASH_PATTERN_GAP_MULTIPLIER,
	DASH_PATTERN_LENGTH_MULTIPLIER,
	LINE_END_ARROW,
	LINE_END_NONE,
	LINE_END_TSHAPE,
	TSHAPE_LENGTH_MULTIPLIER,
} from '@/constants'
import type { Coordinate, DrawingObject } from '@/types'
import React from 'react'
import { angleFromDeltas } from './Geometry.utils'

/**
 * Calculate average angle from a series of points
 * Used for drawing line endings (arrows, T-shapes) in the correct direction
 */
export function calculateAverageAngle(
	points: Array<Coordinate>,
): number {
	if (points.length < 2) return 0

	const numPointsToAverage = Math.min(
		ANGLE_AVERAGE_POINTS,
		points.length - 1,
	)
	const startIndex = points.length - numPointsToAverage - 1

	let totalAngle = 0
	let angleCount = 0

	for (let i = startIndex; i < points.length - 1; i++) {
		const p1 = points[i]
		const p2 = points[i + 1]
		if (!p1 || !p2) continue
		const segmentAngle = angleFromDeltas(p2.x - p1.x, p2.y - p1.y)
		totalAngle += segmentAngle
		angleCount++
	}

	return totalAngle / angleCount
}

/**
 * Draw a single filled arrow arm (rectangle + semicircular cap)
 */
function drawFilledArm(
	ctx: CanvasRenderingContext2D,
	endPoint: Coordinate,
	armAngle: number,
	length: number,
	halfW: number,
): void {
	const tipCenter = {
		x: endPoint.x - length * Math.cos(armAngle),
		y: endPoint.y - length * Math.sin(armAngle),
	}

	const perpX = Math.cos(armAngle + Math.PI / 2)
	const perpY = Math.sin(armAngle + Math.PI / 2)

	const innerA = { x: endPoint.x + halfW * perpX, y: endPoint.y + halfW * perpY }
	const innerB = { x: endPoint.x - halfW * perpX, y: endPoint.y - halfW * perpY }
	const outerB = { x: tipCenter.x - halfW * perpX, y: tipCenter.y - halfW * perpY }

	// Semicircular arc angles (from outerB around to outerA)
	const arcStartAngle = armAngle + Math.PI / 2 + Math.PI
	const arcEndAngle = armAngle - Math.PI / 2

	ctx.beginPath()
	ctx.moveTo(innerB.x, innerB.y)
	ctx.lineTo(outerB.x, outerB.y)
	ctx.arc(tipCenter.x, tipCenter.y, halfW, arcStartAngle, arcEndAngle, true)
	ctx.lineTo(innerA.x, innerA.y)
	ctx.closePath()
	ctx.fill()
}

/**
 * Draw filled arrow head — two paddle/teardrop shapes meeting at the tip
 */
export function drawArrow(
	ctx: CanvasRenderingContext2D,
	endPoint: Coordinate,
	angle: number,
	brushSize: number,
): void {
	const arrowLength = brushSize * ARROW_LENGTH_MULTIPLIER
	const halfW = brushSize / 2

	ctx.save()
	ctx.fillStyle = ctx.strokeStyle as string
	drawFilledArm(ctx, endPoint, angle - ARROW_ANGLE_DEGREES, arrowLength, halfW)
	drawFilledArm(ctx, endPoint, angle + ARROW_ANGLE_DEGREES, arrowLength, halfW)
	ctx.restore()
}

/**
 * Draw filled T-shape — a capsule perpendicular to the route
 */
export function drawTShape(
	ctx: CanvasRenderingContext2D,
	endPoint: Coordinate,
	angle: number,
	brushSize: number,
): void {
	const tLength = brushSize * TSHAPE_LENGTH_MULTIPLIER
	const halfW = brushSize / 2
	const perpAngle = angle + Math.PI / 2

	const perpX = Math.cos(perpAngle)
	const perpY = Math.sin(perpAngle)
	const routeX = Math.cos(angle)
	const routeY = Math.sin(angle)

	const leftCenter = {
		x: endPoint.x - tLength * perpX,
		y: endPoint.y - tLength * perpY,
	}
	const rightCenter = {
		x: endPoint.x + tLength * perpX,
		y: endPoint.y + tLength * perpY,
	}

	// Arc angles for left and right caps
	const leftArcStart = angle + Math.PI
	const leftArcEnd = angle
	const rightArcStart = angle
	const rightArcEnd = angle + Math.PI

	ctx.save()
	ctx.fillStyle = ctx.strokeStyle as string
	ctx.beginPath()
	ctx.moveTo(
		leftCenter.x - halfW * routeX,
		leftCenter.y - halfW * routeY,
	)
	ctx.arc(leftCenter.x, leftCenter.y, halfW, leftArcStart, leftArcEnd, true)
	ctx.lineTo(
		rightCenter.x + halfW * routeX,
		rightCenter.y + halfW * routeY,
	)
	ctx.arc(rightCenter.x, rightCenter.y, halfW, rightArcStart, rightArcEnd, true)
	ctx.closePath()
	ctx.fill()
	ctx.restore()
}

/**
 * Draw line ending (arrow or T-shape) at the end of a path
 */
export function drawLineEnding(
	ctx: CanvasRenderingContext2D,
	lineEnd: 'none' | 'arrow' | 'tShape',
	points: Array<Coordinate>,
	brushSize: number,
): void {
	if (lineEnd === LINE_END_NONE || points.length < 2) return

	const endPoint = points[points.length - 1]
	if (!endPoint) return

	const angle = calculateAverageAngle(points)

	ctx.save()
	ctx.setLineDash([])

	if (lineEnd === LINE_END_ARROW && endPoint) {
		drawArrow(ctx, endPoint, angle, brushSize)
	} else if (lineEnd === LINE_END_TSHAPE && endPoint) {
		drawTShape(ctx, endPoint, angle, brushSize)
	}

	ctx.restore()
}

/**
 * Render an erase stroke by removing pixels in a circular pattern
 */
export function renderEraseStroke(
	ctx: CanvasRenderingContext2D,
	pixelPoints: Array<Coordinate>,
	eraseSize: number,
): void {
	ctx.globalCompositeOperation = COMPOSITE_DESTINATION_OUT
	pixelPoints.forEach((point) => {
		ctx.beginPath()
		ctx.arc(point.x, point.y, eraseSize / 2, 0, Math.PI * 2)
		ctx.fill()
	})
}

/**
 * Render a draw stroke with specified style and line ending
 */
export function renderDrawStroke(
	ctx: CanvasRenderingContext2D,
	drawing: DrawingObject,
	pixelPoints: Array<Coordinate>,
): void {
	ctx.globalCompositeOperation = COMPOSITE_SOURCE_OVER
	ctx.strokeStyle = drawing.color
	ctx.lineWidth = drawing.brushSize
	ctx.lineCap = 'round'
	ctx.lineJoin = 'round'

	// Set line dash pattern
	if (drawing.lineStyle === 'dashed') {
		ctx.setLineDash([
			drawing.brushSize * DASH_PATTERN_LENGTH_MULTIPLIER,
			drawing.brushSize * DASH_PATTERN_GAP_MULTIPLIER,
		])
	} else {
		ctx.setLineDash([])
	}

	// Draw the path
	if (pixelPoints.length === 0) return

	const firstPoint = pixelPoints[0]!

	ctx.beginPath()
	ctx.moveTo(firstPoint.x, firstPoint.y)
	for (let i = 1; i < pixelPoints.length; i++) {
		const point = pixelPoints[i]!
		ctx.lineTo(point.x, point.y)
	}
	ctx.stroke()

	// Draw line ending if applicable
	drawLineEnding(
		ctx,
		drawing.lineEnd,
		pixelPoints,
		drawing.brushSize,
	)
}

/**
 * Calculate distance from a point to a line segment
 * Used for hit testing on drawn lines
 */
export function pointToLineDistance(
	point: Coordinate,
	lineStart: Coordinate,
	lineEnd: Coordinate,
): number {
	const lineLength = Math.sqrt(
		Math.pow(lineEnd.x - lineStart.x, 2) +
		Math.pow(lineEnd.y - lineStart.y, 2),
	)

	if (lineLength === 0) {
		return Math.sqrt(
			Math.pow(point.x - lineStart.x, 2) +
			Math.pow(point.y - lineStart.y, 2),
		)
	}

	const t =
		((point.x - lineStart.x) * (lineEnd.x - lineStart.x) +
			(point.y - lineStart.y) * (lineEnd.y - lineStart.y)) /
		Math.pow(lineLength, 2)

	const closestPoint = {
		x: lineStart.x + t * (lineEnd.x - lineStart.x),
		y: lineStart.y + t * (lineEnd.y - lineStart.y),
	}

	if (t < 0) {
		closestPoint.x = lineStart.x
		closestPoint.y = lineStart.y
	} else if (t > 1) {
		closestPoint.x = lineEnd.x
		closestPoint.y = lineEnd.y
	}

	return Math.sqrt(
		Math.pow(point.x - closestPoint.x, 2) +
		Math.pow(point.y - closestPoint.y, 2),
	)
}

/**
 * Determine if a click point is near any line segment in a drawing
 * Used for selecting/interacting with drawings
 */
export function isPointNearDrawing(
	clickPixelPoint: Coordinate,
	_drawing: DrawingObject,
	pixelPoints: Array<Coordinate>,
	brushSize: number = 15,
): boolean {
	// Use a generous tolerance to make it easier to click on lines
	// Scale with brush size but ensure a minimum of 15 pixels
	const tolerance = Math.max(15, brushSize * 2)

	// Check each line segment in the drawing
	for (let i = 0; i < pixelPoints.length - 1; i++) {
		const p1 = pixelPoints[i]
		const p2 = pixelPoints[i + 1]
		if (!p1 || !p2) continue

		// Calculate the distance from the click point to the line segment
		const distance = pointToLineDistance(clickPixelPoint, p1, p2)

		if (distance <= tolerance) {
			return true
		}
	}

	return false
}

/**
 * Get canvas coordinates from a mouse event
 * Returns pixel coordinates relative to the container
 */
export function getCanvasCoordinates(
	e: React.MouseEvent<HTMLCanvasElement>,
	containerRect: DOMRect,
): { x: number; y: number } {
	return {
		x: e.clientX - containerRect.left,
		y: e.clientY - containerRect.top,
	}
}

/**
 * Convert pointer event coordinates to canvas pixel and feet coordinates.
 * Handles zoom transformation and coordinate system conversion.
 * Used to eliminate duplication in FreehandCapture component.
 */
export function convertPointerToCanvasCoordinates(
	event: React.PointerEvent<HTMLCanvasElement>,
	canvas: HTMLCanvasElement,
	zoom: number,
	coordSystem: {
		pixelsToFeet: (x: number, y: number) => { x: number; y: number }
	},
): {
	canvasPixel: Coordinate
	feet: Coordinate
} | null {
	const ctx = canvas.getContext('2d')
	if (!ctx) return null

	const rect = canvas.getBoundingClientRect()
	const screenRelative = {
		x: event.clientX - rect.left,
		y: event.clientY - rect.top,
	}

	// Convert screen coordinates to canvas buffer pixels
	// Since FreehandCapture is INSIDE the CSS transform container, getBoundingClientRect()
	// already accounts for pan offset. We only need to divide by zoom.
	const canvasPixel = {
		x: screenRelative.x / zoom,
		y: screenRelative.y / zoom,
	}

	// Convert to feet using the canvas pixel position
	const feet = coordSystem.pixelsToFeet(canvasPixel.x, canvasPixel.y)

	return { canvasPixel, feet }
}

/**
 * Convert SVG event coordinates to screen and optionally feet coordinates.
 * Handles SVG element retrieval, bounds checking, and coordinate conversion.
 * Used to eliminate duplication across EditablePlayRenderer and BasePath components.
 *
 * @param event - The pointer or mouse event
 * @param svgElement - The SVG element to use for coordinate calculation
 * @param coordSystem - Optional coordinate system for feet conversion
 * @param zoom - Optional zoom level (default: 1)
 * @param panX - Optional pan X offset (default: 0)
 * @param panY - Optional pan Y offset (default: 0)
 * @returns Object with screen coordinates and optional feet coordinates, or null if SVG unavailable
 */
export function convertSVGEventToCoordinates(
	event: React.PointerEvent | React.MouseEvent,
	svgElement: SVGSVGElement | null | undefined,
	coordSystem?: {
		screenToFeet: (
			x: number,
			y: number,
			zoom: number,
			panX: number,
			panY: number,
		) => Coordinate
	},
	zoom: number = 1,
	panX: number = 0,
	panY: number = 0,
): {
	screen: Coordinate
	feet?: Coordinate
} | null {
	if (!svgElement) return null

	const rect = svgElement.getBoundingClientRect()
	const screen = {
		x: event.clientX - rect.left,
		y: event.clientY - rect.top,
	}

	// Convert to feet if coordinate system provided
	const feet = coordSystem
		? coordSystem.screenToFeet(screen.x, screen.y, zoom, panX, panY)
		: undefined

	return { screen, feet }
}
